import NStatusChip from "@/components/common/NStatusChip";
import CheckboxGroupInput from "@/components/inputs/CheckboxGroupInput";
import DateInput from "@/components/inputs/DateInput";
import SelectInput from "@/components/inputs/SelectInput";
import SwitchInput from "@/components/inputs/SwitchInput";
import SITable from "@/components/field/insights/SITable";
import SITrendline from "@/components/field/insights/SITrendline";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { CropConfiguration, CropStage } from "@/types/cropConfiguration";
import { TrendlineTable } from "@/types/plot";
import {
    faChartBar,
    faCheck,
    faFilter,
    faSliders,
    faTable,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    Chip,
    Divider,
    Select,
    Selection,
    SelectItem,
    Spinner,
    Tooltip,
} from "@heroui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { array, boolean, date, mixed, object, string } from "yup";
import { ImageAnalytics } from "@/types/imagery";
import SIBar from "@/components/field/insights/SIBar";
import SIBarTable from "@/components/field/insights/SIBarTable";
import NUptake from "@/components/field/insights/NUptake";
import NUptakeTable from "@/components/field/insights/NUptakeTable";
import VegetativeTable from "@/components/field/insights/VegetativeTable";
import VegetativeTrendline from "@/components/field/insights/VegetativeTrendline";
import { ReportedGrowthStage } from "@/types/reportedGrowthStage";
import useAuth from "@/lib/auth/useAuth";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import SoilProperties from "@/components/field/insights/SoilProperties";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import TissueProperties from "@/components/field/insights/TissueProperties";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { Resource } from "@/types/user";
import WeatherCharts from "@/components/field/insights/WeatherCharts";

export const Route = createFileRoute("/dashboard/fields/$fieldId/insights/")({
    component: RouteComponent,
});

const schema = object().shape({
    graph: string()
        .required()
        .oneOf([
            "trendline",
            "bar",
            "uptake",
            "vegetative",
            "soil_properties",
            "tissue_properties",
            "weather",
        ]),
    showTable: boolean().required(),
    showFilters: boolean().required(),
    dateRange: mixed(),
    startDate: date(),
    endDate: date(),
    pairIds: array(),
    rangeOnTrendline: boolean().required(),
    showBulkSI: boolean().required(),
    showVirtualSI: boolean().required(),
});

function RouteComponent() {
    const { userIsSentinel, isAtLeastDetail } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { t } = useTranslation();
    const { formatDateMMDDYYYY } = useI18n();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights/",
    });

    const { fieldQuery, selectedCropCycle } = useFieldDashboard() ?? {};

    const cropConfigQuery = useGetRequest<CropConfiguration>({
        url: `crop_configurations/${fieldQuery?.data?.crop?.id}/`,
        enabled: !!fieldQuery?.data?.crop?.id,
    });

    const growthStages = useMemo(
        () => cropConfigQuery?.data?.growth_stages ?? [],
        [cropConfigQuery?.data?.growth_stages],
    );

    const hasCrop = useMemo(() => {
        return !!growthStages?.length;
    }, [growthStages?.length]);

    const { selectedImage } = useFieldDashboard() ?? {};

    const f = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            graph: "trendline",
            showTable: false,
            showFilters: false,
            rangeOnTrendline: true,
            showBulkSI: false,
            showVirtualSI: false,
        },
        mode: "onChange",
    });

    const [graph, showFilters, showTable, startDate, endDate] = f.watch([
        "graph",
        "showFilters",
        "showTable",
        "startDate",
        "endDate",
        "pairIds",
    ]);

    useEffect(() => {
        if (selectedCropCycle) {
            f.setValue(
                "startDate",
                (dayjs(selectedCropCycle.end_date) < getTodayWithTimeTravel()
                    ? dayjs(selectedCropCycle.start_date)
                    : getTodayWithTimeTravel().subtract(1, "month")
                ).toDate(),
            );
            f.setValue(
                "endDate",
                dayjs(selectedCropCycle?.end_date) < getTodayWithTimeTravel()
                    ? dayjs(selectedCropCycle.end_date).toDate()
                    : getTodayWithTimeTravel().toDate(),
            );
        }
    }, [f, selectedCropCycle]);

    const lastTwoMonths = useMemo(() => {
        if (!startDate || !endDate) return false;

        return dayjs(startDate).add(2, "months").isSame(dayjs(endDate), "days");
    }, [endDate, startDate]);

    const lastTwoWeeks = useMemo(() => {
        if (!startDate || !endDate) return false;

        return dayjs(startDate).add(2, "weeks").isSame(dayjs(endDate), "days");
    }, [endDate, startDate]);

    const tableQuery = useGetRequest<TrendlineTable[]>({
        url: `fields/${fieldId}/trendline_table/`,
        enabled: !!fieldId,
        params: {
            values: "si",
            groupby: "block",
        },
    });

    const pairIdOptions = useMemo(() => {
        return sortArrayByKey(
            [...new Set(tableQuery?.data?.map((row) => row.pair_id) ?? [])].map(
                (pair) => ({ id: pair, description: pair }),
            ),
            "id",
        );
    }, [tableQuery?.data]);

    const imageAnalyticsQuery = useGetRequest<ImageAnalytics>({
        url: `imagery/${selectedImage?.id}/analytics/`,
        enabled: !!selectedImage?.id,
    });

    const reportedGrowthStagesQuery = useGetRequest<ReportedGrowthStage[]>({
        url: `reported_growth_stages/`,
        enabled: !!fieldId,
        params: {
            field: fieldId,
            start_date: dayjs(selectedCropCycle?.start_date).isValid()
                ? dayjs(selectedCropCycle?.start_date).format("YYYY-MM-DD")
                : undefined,
            end_date: dayjs(selectedCropCycle?.end_date).isValid()
                ? dayjs(selectedCropCycle?.end_date).format("YYYY-MM-DD")
                : undefined,
        },
    });

    useEffect(() => {
        if (reportedGrowthStagesQuery?.data?.length) {
            const growthStageOnDate = reportedGrowthStagesQuery?.data?.find(
                (row) =>
                    dayjs(row.date).isSame(
                        dayjs(selectedImage?.capture_date),
                        "day",
                    ),
            );
            if (growthStageOnDate) {
                setSelectedGrowthStage(
                    new Set([growthStageOnDate.growth_stage]),
                );
                return;
            }
        }
        setSelectedGrowthStage(new Set([]));
    }, [reportedGrowthStagesQuery?.data, selectedImage?.capture_date]);

    const [selectedGrowthStage, setSelectedGrowthStage] = useState<Selection>();
    const setGrowthStage = useMutateRequest({
        successMessage: t("success.growth_stage_reported_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_report_growth_stage", {
            ns: "messages",
        }),
    });

    const fieldCropStageQuery = useGetRequest<CropStage | null>({
        url: `fields/${fieldId}/crop_stage/`,
        enabled: !!fieldId && !!selectedImage?.capture_date,
        params: {
            target_date: dayjs(selectedImage?.capture_date).format(
                "YYYY-MM-DD",
            ),
        },
    });

    const submitGrowthStage = useCallback(
        (stage: Selection) => {
            if (!stage || stage === "all") return;
            setGrowthStage.mutate(
                {
                    url: `reported_growth_stages/`,
                    method: "POST",
                    data: {
                        id: null,
                        field: fieldId,
                        growth_stage: [...stage][0],
                        date: dayjs(selectedImage?.capture_date).format(
                            "YYYY-MM-DD",
                        ),
                    },
                },
                {
                    onSuccess: () => {
                        reportedGrowthStagesQuery.refetch();
                        fieldCropStageQuery.refetch();
                    },
                },
            );
        },
        [
            setGrowthStage,
            fieldId,
            selectedImage?.capture_date,
            reportedGrowthStagesQuery,
            fieldCropStageQuery,
        ],
    );

    const popOverContent = useMemo(() => {
        if (!fieldCropStageQuery?.data) return null;
        return (
            <div className="grid grid-cols-2 gap-2">
                <div>Growth Stage:</div>
                <div>{fieldCropStageQuery?.data?.growth_stage}</div>
                <div>DAP: </div>
                <div>{fieldCropStageQuery?.data?.days_after_planting}</div>
                <div>GDD: </div>
                <div>{fieldCropStageQuery?.data?.growing_degree_days}</div>
                <div>Nitrogen Flux:</div>
                <div>{fieldCropStageQuery?.data?.nitrogen_flux}</div>
                <div>Vegetation Index: </div>
                <div>{fieldCropStageQuery?.data?.vegetation_index}</div>
                <div>KCB:</div>
                <div>{fieldCropStageQuery?.data?.kcb}</div>
            </div>
        );
    }, [fieldCropStageQuery?.data]);

    const { samplesEnabled, bulkSIEnabled, virtualSIEnabled, weatherEnabled } =
        useFeatureFlag();

    const insightsOptions = useMemo(() => {
        const options = [
            {
                description: t("siTrendline", { ns: "insights" }),
                id: "trendline",
            },
            { description: t("siBar", { ns: "insights" }), id: "bar" },
            { description: t("nUptake", { ns: "insights" }), id: "uptake" },
            {
                description: t("vegetativeTrendline", { ns: "insights" }),
                id: "vegetative",
            },
        ];
        if (samplesEnabled) {
            options.push({
                description: t("soilProperties", { ns: "insights" }),
                id: "soil_properties",
            });
            options.push({
                description: t("tissueProperties", { ns: "insights" }),
                id: "tissue_properties",
            });
        }
        if (weatherEnabled) {
            options.push({
                description: t("weather", { ns: "insights" }),
                id: "weather",
            });
        }
        return options;
    }, [samplesEnabled, t, weatherEnabled]);

    const defaultFallbackDate = useMemo(() => {
        const defaultPlantingMonth = 5;
        const defaultPlantingDay = 1;

        const targetDate = getTodayWithTimeTravel();
        const thisYearDefaultDate = dayjs()
            .year(targetDate.year())
            .month(defaultPlantingMonth - 1)
            .date(defaultPlantingDay)
            .startOf("day");

        if (targetDate.isAfter(thisYearDefaultDate)) {
            return thisYearDefaultDate.toDate();
        }
        return dayjs()
            .year(targetDate.year() - 1)
            .month(defaultPlantingMonth - 1)
            .date(defaultPlantingDay)
            .startOf("day")
            .toDate();
    }, []);

    return (
        <div className="flex flex-col p-2">
            <div className="flex flex-row justify-between items-start">
                <div className="flex flex-col gap-1 flex-1">
                    <div>
                        {t("imageDate", { ns: "insights" })}{" "}
                        <span>
                            {formatDateMMDDYYYY(selectedImage?.capture_date)}
                        </span>
                    </div>
                    <div>
                        {t("nStatus", { ns: "insights" })}{" "}
                        {selectedImage?.n_status ? (
                            <NStatusChip nStatus={selectedImage?.n_status} />
                        ) : (
                            <span className="italic">None</span>
                        )}{" "}
                    </div>
                    <div className="flex flex-row items-center gap-2 ">
                        <div>
                            {t("reportedGrowthStage", { ns: "insights" })}:{" "}
                        </div>
                        {hasCrop ? (
                            <Select
                                className=" max-w-[10rem]"
                                size="sm"
                                aria-label="Growth Stage"
                                selectedKeys={selectedGrowthStage}
                                onSelectionChange={(e) => {
                                    setSelectedGrowthStage(e);
                                    submitGrowthStage(e);
                                }}
                            >
                                {growthStages?.map((stage) => {
                                    return (
                                        <SelectItem key={stage}>
                                            {stage}
                                        </SelectItem>
                                    );
                                })}
                            </Select>
                        ) : (
                            <div className="text-xs italic">
                                {t("growthStageError", { ns: "insights" })}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row items-center gap-2 ">
                        <div>
                            {t("estimatedGrowthStage", { ns: "insights" })}:{" "}
                        </div>

                        <Tooltip
                            content={<div>{popOverContent}</div>}
                            delay={100}
                            isDisabled={!userIsSentinel}
                            closeDelay={100}
                        >
                            <Chip color="primary">
                                <div className="flex flex-row gap-2 items-center">
                                    {fieldCropStageQuery?.isLoading ? (
                                        <Spinner size="sm" color="default" />
                                    ) : (
                                        (fieldCropStageQuery?.data
                                            ?.growth_stage ?? "--")
                                    )}
                                </div>
                            </Chip>
                        </Tooltip>
                    </div>
                    <div className="text-xs italic">
                        {fieldQuery?.data?.active_planting_date
                            ? `${t("plantedOn", { ns: "insights" })} ${dayjs(fieldQuery?.data?.active_planting_date).format("ll")}.`
                            : `${t("noPlantingOperation", { ns: "insights" })} ${dayjs(
                                  cropConfigQuery?.data
                                      ?.default_planting_date ??
                                      defaultFallbackDate,
                              ).format("ll")}.`}
                    </div>
                </div>
                {isAtLeastDetail(Resource.INSIGHTS_CONFIGURATION) && (
                    <Button
                        isIconOnly
                        variant="flat"
                        as={Link}
                        to="/dashboard/fields/$fieldId/insights/settings"
                        params={
                            {
                                fieldId,
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            } as any
                        }
                        aria-label="Insights Settings"
                    >
                        <FontAwesomeIcon icon={faSliders} />
                    </Button>
                )}
            </div>
            <Divider className="mt-2" />
            <div className="flex flex-col gap-2 mt-3 ">
                <FormProvider {...f}>
                    <div className="flex flex-row gap-2 items-end text-sm">
                        <SelectInput
                            disallowEmptySelection
                            name="graph"
                            labelPlacement="outside"
                            label={t("graphType", { ns: "insights" })}
                            size="sm"
                            options={insightsOptions}
                        />
                        <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            onPress={() =>
                                f.setValue("showFilters", !showFilters)
                            }
                            aria-label="Toggle Filters"
                            color={showFilters ? "primary" : "default"}
                        >
                            <FontAwesomeIcon icon={faFilter} />
                        </Button>
                        <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            onPress={() => f.setValue("showTable", !showTable)}
                            aria-label="Toggle Table"
                        >
                            <FontAwesomeIcon
                                icon={!showTable ? faTable : faChartBar}
                            />
                        </Button>
                    </div>
                    {showFilters && (
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row self-end justify-between w-full  gap-2 items-center">
                                <div className="font-semibold text-sm">
                                    {t("dateRange", { ns: "insights" })}
                                </div>
                                <div className="flex flex-row gap-2">
                                    <Button
                                        color={
                                            lastTwoMonths
                                                ? "success"
                                                : "default"
                                        }
                                        variant="flat"
                                        size="sm"
                                        onPress={() => {
                                            f.setValue(
                                                "startDate",
                                                dayjs()
                                                    .subtract(2, "months")
                                                    .toDate(),
                                            );
                                            f.setValue(
                                                "endDate",
                                                dayjs().toDate(),
                                            );
                                        }}
                                    >
                                        {lastTwoMonths && (
                                            <FontAwesomeIcon icon={faCheck} />
                                        )}
                                        {t("lastTwoMonths", { ns: "insights" })}
                                    </Button>
                                    <Button
                                        variant="flat"
                                        color={
                                            lastTwoWeeks ? "success" : "default"
                                        }
                                        size="sm"
                                        onPress={() => {
                                            f.setValue(
                                                "startDate",
                                                dayjs()
                                                    .subtract(2, "weeks")
                                                    .toDate(),
                                            );
                                            f.setValue(
                                                "endDate",
                                                dayjs().toDate(),
                                            );
                                        }}
                                    >
                                        {lastTwoWeeks && (
                                            <FontAwesomeIcon icon={faCheck} />
                                        )}
                                        {t("lastTwoWeeks", { ns: "insights" })}
                                    </Button>
                                </div>
                            </div>
                            <div className="flex flex-row justify-evenly  gap-2">
                                <DateInput
                                    name="startDate"
                                    label={t("startDate", { ns: "insights" })}
                                    size="sm"
                                />
                                <DateInput
                                    name="endDate"
                                    label={t("endDate", { ns: "insights" })}
                                    size="sm"
                                />
                            </div>
                            {["trendline", "bar"].includes(graph) && (
                                <div className="flex flex-col">
                                    <div className="font-semibold text-sm">
                                        {t("plotPairs", { ns: "insights" })}
                                    </div>
                                    <CheckboxGroupInput
                                        name="pairIds"
                                        options={pairIdOptions}
                                        orientation="horizontal"
                                    />
                                </div>
                            )}
                            {graph === "trendline" && (
                                <div className="flex flex-row items-center gap-2">
                                    <SwitchInput
                                        id="rangeOnTrendline"
                                        name="rangeOnTrendline"
                                        size="sm"
                                        defaultSelected={true}
                                        label={t("showPlotRanges", {
                                            ns: "insights",
                                        })}
                                    />

                                    {bulkSIEnabled && (
                                        <SwitchInput
                                            id="showBulkSI"
                                            name="showBulkSI"
                                            size="sm"
                                            defaultSelected={false}
                                            label={t("showBulkSI", {
                                                ns: "insights",
                                            })}
                                        />
                                    )}

                                    {virtualSIEnabled && (
                                        <SwitchInput
                                            id="showVirtualSI"
                                            name="showVirtualSI"
                                            size="sm"
                                            defaultSelected={false}
                                            label={t("virtualSI", {
                                                ns: "insights",
                                            })}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {graph == "trendline" && (
                        <>
                            {showTable ? (
                                <SITable trendlineQuery={tableQuery} />
                            ) : (
                                <SITrendline
                                    fieldId={fieldId}
                                    senstiivity={
                                        fieldQuery?.data?.insight_sensitivity
                                    }
                                    tableQuery={tableQuery}
                                />
                            )}
                        </>
                    )}
                    {graph == "bar" && (
                        <>
                            {showTable ? (
                                <SIBarTable
                                    imageAnalyticsQuery={imageAnalyticsQuery}
                                />
                            ) : (
                                <SIBar
                                    imageAnalyticsQuery={imageAnalyticsQuery}
                                />
                            )}
                        </>
                    )}
                    {graph == "uptake" && (
                        <>{showTable ? <NUptakeTable /> : <NUptake />}</>
                    )}
                    {graph == "vegetative" && (
                        <>
                            {showTable ? (
                                <VegetativeTable />
                            ) : (
                                <VegetativeTrendline />
                            )}
                        </>
                    )}
                    {graph == "soil_properties" && <SoilProperties />}
                    {graph == "tissue_properties" && <TissueProperties />}
                    {graph == "weather" && <WeatherCharts />}
                </FormProvider>
            </div>
        </div>
    );
}
